import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const CreateUsers = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    is_active: true,
    branch_id: '',
    warehouse_id: ''
  })
  const [branches, setBranches] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [loadingWarehouses, setLoadingWarehouses] = useState(false)

  useEffect(() => {
    // Fetch branches when role requires it
    if (formData.role === 'branch_manager' || formData.role === 'cashier') {
      fetchBranches()
    } else {
      setBranches([])
      setFormData(prev => ({ ...prev, branch_id: '' }))
    }

    // Fetch warehouses when role requires it
    if (formData.role === 'warehouse_manager') {
      fetchWarehouses()
    } else {
      setWarehouses([])
      setFormData(prev => ({ ...prev, warehouse_id: '' }))
    }
  }, [formData.role])

  const fetchBranches = async () => {
    setLoadingBranches(true)
    try {
      const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
      setBranches(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching branches:', error)
      // If API doesn't exist yet, set empty array
      setBranches([])
    } finally {
      setLoadingBranches(false)
    }
  }

  const fetchWarehouses = async () => {
    setLoadingWarehouses(true)
    try {
      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      setWarehouses(response.data.results || response.data || [])
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      // If API doesn't exist yet, set empty array
      setWarehouses([])
    } finally {
      setLoadingWarehouses(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Prepare payload according to backend expectations
      const payload = {
        email: formData.email,
        role: formData.role,
        first_name: formData.first_name,
        last_name: formData.last_name,
        is_active: formData.is_active
      }

      // Add branch_id or warehouse_id based on role
      if (formData.role === 'branch_manager' || formData.role === 'cashier') {
        if (!formData.branch_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: `Branch is required for ${formData.role} role`
          })
          setLoading(false)
          return
        }
        payload.branch_id = parseInt(formData.branch_id)
      } else if (formData.role === 'warehouse_manager') {
        if (!formData.warehouse_id) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: 'Warehouse is required for warehouse_manager role'
          })
          setLoading(false)
          return
        }
        payload.warehouse_id = parseInt(formData.warehouse_id)
      }

      await apiClient.post(API_ENDPOINTS.CREATE_USER, payload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'User created successfully! A temporary password has been sent to their email.',
        confirmButtonText: 'OK'
      })

      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        is_active: true,
        branch_id: '',
        warehouse_id: ''
      })
    } catch (error) {
      console.error('Error creating user:', error)
      const errorMessage = error.response?.data?.email?.[0] ||
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to create user. Please try again.'

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Create Users</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Users</li>
            <li className="breadcrumb-item active">Create Users</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-person-plus me-2"></i>
                  Create New User
                </h5>

                <div className="alert alert-info" role="alert">
                  <i className="bi bi-info-circle me-2"></i>
                  A temporary password will be automatically generated and sent to the user's email address.
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label htmlFor="first_name" className="col-sm-2 col-form-label">First Name</label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="last_name" className="col-sm-2 col-form-label">Last Name</label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="email" className="col-sm-2 col-form-label">Email</label>
                    <div className="col-sm-10">
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="role" className="col-sm-2 col-form-label">Role</label>
                    <div className="col-sm-10">
                      <select
                        className="form-select"
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Role</option>
                        <option value="branch_manager">Branch Manager</option>
                        <option value="warehouse_manager">Warehouse Manager</option>
                        <option value="cashier">Cashier</option>
                        <option value="auditor">Auditor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>

                  {/* Branch field - shown for branch_manager and cashier */}
                  {(formData.role === 'branch_manager' || formData.role === 'cashier') && (
                    <div className="row mb-3">
                      <label htmlFor="branch_id" className="col-sm-2 col-form-label">Branch <span className="text-danger">*</span></label>
                      <div className="col-sm-10">
                        <select
                          className="form-select"
                          id="branch_id"
                          name="branch_id"
                          value={formData.branch_id}
                          onChange={handleChange}
                          required
                          disabled={loadingBranches}
                        >
                          <option value="">{loadingBranches ? 'Loading branches...' : 'Select Branch'}</option>
                          {branches.map(branch => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name || `Branch ${branch.id}`}
                            </option>
                          ))}
                        </select>
                        {branches.length === 0 && !loadingBranches && (
                          <small className="text-muted">No branches available. Please add branches first.</small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Warehouse field - shown for warehouse_manager */}
                  {formData.role === 'warehouse_manager' && (
                    <div className="row mb-3">
                      <label htmlFor="warehouse_id" className="col-sm-2 col-form-label">Warehouse <span className="text-danger">*</span></label>
                      <div className="col-sm-10">
                        <select
                          className="form-select"
                          id="warehouse_id"
                          name="warehouse_id"
                          value={formData.warehouse_id}
                          onChange={handleChange}
                          required
                          disabled={loadingWarehouses}
                        >
                          <option value="">{loadingWarehouses ? 'Loading warehouses...' : 'Select Warehouse'}</option>
                          {warehouses.map(warehouse => (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name || `Warehouse ${warehouse.id}`}
                            </option>
                          ))}
                        </select>
                        {warehouses.length === 0 && !loadingWarehouses && (
                          <small className="text-muted">No warehouses available. Please add warehouses first.</small>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Status</label>
                    <div className="col-sm-10">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="is_active"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="is_active">
                          Active (User can login immediately)
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          'Create User'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default CreateUsers

